import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react"
import { Alert, Button, Container, Nav, NavItem, NavLink, Spinner } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { getCompaniesAll } from "store/actions"
import { getAdminMustRead } from "helpers/mustReadApi"
import { draftReducer, initDraft, buildOperations } from "./mustReadDraft"
import MustReadItemList from "./MustReadItemList"
import MustReadItemModal from "./MustReadItemModal"
import MustReadPublishModal from "./MustReadPublishModal"
import MustReadStatusTable from "./MustReadStatusTable"
import MustReadVersionHistoryModal from "./MustReadVersionHistoryModal"
import "components/MustRead/mustRead.css"

const MustReadAdmin = () => {
  const reduxDispatch = useDispatch()
  const companies = useSelector((s) => s.Company?.allCompaniesData) || []
  const companiesById = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies])

  const [tab, setTab] = useState("docs")
  const [state, dispatch] = useReducer(draftReducer, [], initDraft)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [editing, setEditing] = useState(undefined)
  const [publishing, setPublishing] = useState(false)
  const [historyRow, setHistoryRow] = useState(null)

  const pendingCount = buildOperations(state).length

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const data = await getAdminMustRead()
      dispatch({ type: "reset", items: data.items })
    } catch (e) {
      setLoadError("Could not load Onboarding.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    document.title = "Onboarding - ColdBlock Technologies Inc."
    reduxDispatch(getCompaniesAll({ all: true }))
    load()
  }, [load, reduxDispatch])

  useEffect(() => {
    if (!pendingCount) return
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [pendingCount])

  const handleSave = (values, { contentChanged }) => {
    if (editing === null) dispatch({ type: "add", row: values })
    else dispatch({ type: "edit", key: editing.key, patch: values, contentChanged })
    setEditing(undefined)
  }

  const handlePublished = async (result) => {
    setPublishing(false)
    toast.success(
      result?.emailRecipients ? `Published. Emailing ${result.emailRecipients} users.` : "Published."
    )
    await load()
  }

  return (
    <div className="page-content">
      <Container fluid style={{ maxWidth: 1100 }}>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h1 className="mr-heading h3 mb-1">Onboarding</h1>
            <p className="text-muted mb-0">Safety and setup documents shown to company, public and distributor users.</p>
          </div>
          {tab === "docs" && (
            <Button className="mr-btn-primary" onClick={() => setEditing(null)}>
              <i className="bx bx-plus me-1" /> Add doc
            </Button>
          )}
        </div>

        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink href="#" active={tab === "docs"} onClick={(e) => { e.preventDefault(); setTab("docs") }}>Docs</NavLink>
          </NavItem>
          <NavItem>
            <NavLink href="#" active={tab === "status"} onClick={(e) => { e.preventDefault(); setTab("status") }}>Review status</NavLink>
          </NavItem>
        </Nav>

        {tab === "docs" ? (
          <>
            {loadError && <Alert color="danger">{loadError}</Alert>}
            <div className="mr-card">
              {loading ? (
                <div className="text-center p-5"><Spinner /></div>
              ) : (
                <MustReadItemList
                  rows={state.rows}
                  server={state.server}
                  companiesById={companiesById}
                  onEdit={(row) => setEditing(row)}
                  onRetire={(key) => dispatch({ type: "retire", key })}
                  onRestore={(key) => dispatch({ type: "restore", key })}
                  onMove={(from, to) => dispatch({ type: "move", from, to })}
                  onHistory={(row) => setHistoryRow(row)}
                />
              )}
            </div>
            {pendingCount > 0 && (
              <div className="mr-sticky-bar" role="status">
                <span className="fw-semibold">
                  {pendingCount} unpublished {pendingCount === 1 ? "change" : "changes"}
                </span>
                <div className="d-flex gap-2">
                  <Button outline color="secondary" onClick={() => dispatch({ type: "discard" })}>Discard</Button>
                  <Button className="mr-btn-primary" onClick={() => setPublishing(true)}>Review & publish</Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="mr-card">
            <MustReadStatusTable companies={companies} />
          </div>
        )}

        <MustReadItemModal
          isOpen={editing !== undefined}
          row={editing || null}
          companies={companies}
          onCancel={() => setEditing(undefined)}
          onSave={handleSave}
        />
        <MustReadPublishModal
          isOpen={publishing}
          state={state}
          dispatch={dispatch}
          onCancel={() => setPublishing(false)}
          onPublished={handlePublished}
        />
        <MustReadVersionHistoryModal
          isOpen={!!historyRow}
          itemId={historyRow?.id}
          title={historyRow?.title}
          contentType={historyRow?.contentType}
          onClose={() => setHistoryRow(null)}
        />
      </Container>
    </div>
  )
}

export default MustReadAdmin
